/**
 * Goal-first flow — voice + copy module.
 *
 * VOICE RULE (read before editing):
 * Decision language throughout — verbs over nouns, outcomes over features,
 * no survey jargon. Say "evidence", "answer", "verdict" — never "responses",
 * "instrument", or "questionnaire item". Every string should read like it is
 * helping someone make a call, not fill in a form.
 *
 * User-facing copy is resolved through UI i18n (`app` namespace) so the
 * avatar language switcher updates this screen. English source lives in
 * `i18n/locales/en/app.json` under `creation.*`.
 *
 * Cache scope: one-shot per render; not an LLM prompt.
 */

import i18n from '@/i18n'

import type { GoalId } from './types'

function tApp(key: string, options?: Record<string, unknown>): string {
  return String(i18n.t(key, { ns: 'app', ...options }))
}

function goalCopy(id: GoalId) {
  return {
    get title() {
      return tApp(`creation.goals.${id}.title`)
    },
    get subtitle() {
      return tApp(`creation.goals.${id}.subtitle`)
    },
    get methodTag() {
      return tApp(`creation.goals.${id}.methodTag`)
    },
  }
}

export const goalScreenStrings = {
  get title() {
    return tApp('creation.screen.title')
  },
  get subtitle() {
    return tApp('creation.screen.subtitle')
  },
  get comingSoon() {
    return tApp('creation.screen.comingSoon')
  },
  get unavailable() {
    return tApp('creation.screen.unavailable')
  },
} as const

export const goalCardStrings = {
  lp_conversion: goalCopy('lp_conversion'),
  ad_creative: goalCopy('ad_creative'),
  brand_perception: goalCopy('brand_perception'),
  concept_validation: goalCopy('concept_validation'),
  pricing_packaging: goalCopy('pricing_packaging'),
  claim_validation: goalCopy('claim_validation'),
  pain_ranking: goalCopy('pain_ranking'),
  explore: goalCopy('explore'),
}

export const goalCategoryStrings = {
  get test() {
    return tApp('creation.categories.test')
  },
  get brand() {
    return tApp('creation.categories.brand')
  },
  get explore() {
    return tApp('creation.categories.explore')
  },
} as const

export const goalGridStrings = {
  pinAriaLabel: (title: string) => tApp('creation.grid.pinAria', { title }),
  get unpinAriaLabel() {
    return tApp('creation.grid.unpinAria')
  },
} as const

export const deliveryStrings = {
  get title() {
    return tApp('creation.delivery.title')
  },
  get subtitle() {
    return tApp('creation.delivery.subtitle')
  },
  deck: {
    get title() {
      return tApp('creation.delivery.deck')
    },
  },
  twinChat: {
    get title() {
      return tApp('creation.delivery.twinChat')
    },
  },
} as const

export const chipStrings = {
  get goalAriaLabel() {
    return tApp('creation.chip.goalAria')
  },
  get languageAriaLabel() {
    return tApp('creation.chip.languageAria')
  },
  get languageCoachmarkBody() {
    return tApp('creation.chip.coachmarkBody')
  },
  get languageCoachmarkDismissAriaLabel() {
    return tApp('creation.chip.coachmarkDismiss')
  },
} as const

export const setupStrings = {
  get askPrefix() {
    return tApp('creation.setup.askPrefix')
  },
  get languageInfix() {
    return tApp('creation.setup.languageInfix')
  },
  get audienceEmpty() {
    return tApp('creation.setup.audienceEmpty')
  },
  get audienceLoading() {
    return tApp('creation.setup.audienceLoading')
  },
  // Pass a pre-formatted count, the same way ComposeModal does. A raw
  // `{{count}}` interpolation prints "1200 twins" instead of "1,200 twins":
  // the thousands separator was lost when this copy moved to i18n.
  twinsCount: (count: number) =>
    tApp('creation.setup.twinsCount', {
      count,
      formatted: count.toLocaleString(i18n.language),
    }),
  filteredTwinsCount: (count: number) =>
    tApp('creation.setup.filteredTwinsCount', {
      count,
      formatted: count.toLocaleString(i18n.language),
    }),
  get modalTitle() {
    return tApp('creation.setup.modalTitle')
  },
  get modalSubtitle() {
    return tApp('creation.setup.modalSubtitle')
  },
  get modalSearchPlaceholder() {
    return tApp('creation.setup.modalSearchPlaceholder')
  },
  get modalEmpty() {
    return tApp('creation.setup.modalEmpty')
  },
  get modalNoAudiences() {
    return tApp('creation.setup.modalNoAudiences')
  },
  get modalRefineAudience() {
    return tApp('creation.setup.modalRefineAudience')
  },
  get modalContinue() {
    return tApp('creation.setup.modalContinue')
  },
  get modalSaveAudience() {
    return tApp('creation.setup.modalSaveAudience')
  },
  get bookmarkedBadge() {
    return tApp('creation.setup.bookmarkedBadge')
  },
} as const
